import React, { useState } from "react"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import InvestigateIcon from "../../icons/investigate.svg"
import { createUseStyles } from "react-jss"
import GenerativeAiIcon from "../../icons/generating-ai-image.png"
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  TableSortLabel,
  Grid, // Import Grid for layout
} from "@mui/material"
import { IIssueTrackerColumn } from "../../util/interface"

// Custom styles using react-jss
const useStyles = createUseStyles({
  wrapper: {
    fontSize: 12,
  },
  root: {
    "& .MuiTableCell-head": {
      backgroundColor: "#fbfbfb",
    },
    height: 85,
  },
  heading: {
    fontSize: 20,
    fontWeight: 700,
    fontFamily: "Roboto",
    color: "black",
    textAlign: "center",
  },
  subHeading: {
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "Roboto",
    color: "black",
    textAlign: "center",
  },
  borderRight: {
    borderRight: "1px solid lightgray",
  },
  investigateButton: {
    background: "linear-gradient(90deg, #67F0FF 0%, #CCC0DD 50%, #FFA0BB 100%)",
    color: "var(--text-primary-color)",
    fontWeight: 600,
    fontSize: "12px",
    "&:hover": {
      backgroundColor: "var(--primary-dp-dark-90)",
    },
  },
  sortLabelIcon: {
    display: "flex",
    flexDirection: "column", // Stack icons vertically
  },
  upperPartition: {
    background: `url(${GenerativeAiIcon}) no-repeat center center`,
    opacity: 20,
    height: 90,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "30px",
  },
  lowerPartition: {
    paddingTop: "30px",
    paddingLeft: "20px",
    paddingRight: "20px",
    fontFamily: "Roboto",
    fontSize: "16px",
    height: 320,
    fontWeight: 300,
    textAlign: "left",
  },
  boldText: {
    fontWeight: 700,
  },
  issueDetailHeader: {
    backgroundColor: "var(--primary-dp-color) !important", // Ensure background color is applied
    color: "white", // Text color for better visibility
    fontWeight: 600, // Ensure text is bold
  },
  issueDetailCell: {
    backgroundColor: "var(--primary-dp-color-10)", // Background color for Issue Detail column cells
    fontWeight: 600,
    // Background color for Issue Detail column cells
  },
  dialog_box: {
    padding: 0,
  },
  dialog_box_content: {
    padding: 0,
  },
})

// Define columns for the table
const columns: IIssueTrackerColumn[] = [
  { id: "serial_no", label: "Serial.No", minWidth: 100, align: "left" },
  { id: "date", label: "Date", minWidth: 100, align: "left" },
  { id: "usecase", label: "Use Case", minWidth: 100, align: "center" },
  {
    id: "pipelineName",
    label: "Pipeline Name",
    minWidth: 150,
    align: "center",
  },
  { id: "pipelineId", label: "Pipeline Id", minWidth: 150, align: "center" },
  { id: "issueDetail", label: "Issue Detail", minWidth: 200, align: "center" },
  { id: "reportedBy", label: "Reported By", minWidth: 150, align: "left" },
  {
    id: "aiAgentInvestigation",
    label: "AI Agent Investigation",
    minWidth: 200,
    align: "left",
  },
]

// Mock data for the table
const mockData = [
  {
    serial_no: "01",
    date: "13/11/2023",
    usecase: "Promo",
    pipelineName: "promo_orchestration_pipeline_1",
    pipelineId: "c7474d03-d7d3-42e9-ad26-8d6b",
    issueDetail:
      "Data missing for XYZ solution in Finance Datamart table fact_sales after pipeline execution. ID: c7474d03-d7d3-42e9-ad26-8d6b",
    reportedBy: "Shubham P.",
    aiAgentInvestigation: "Investigate",
    issueId: "fxhgy5-cucu-xghx-chss-67ahjch",
  },
  {
    serial_no: "02",
    date: "15/11/2023",
    usecase: "Promo",
    pipelineName: "promo_orchestration_pipeline_2",
    pipelineId: "fxhgy5-cucu-xghx-chss-67ahjch",
    issueDetail:
      "Data pipeline failure due to unhandled exceptions in custom scripts.",
    reportedBy: "Shubham P.",
    aiAgentInvestigation: "Investigate",
    issueId: "vjkk7y5-cucu-xghx-chss-67ahjch",
  },
  {
    serial_no: "03",
    date: "13/12/2023",
    usecase: "Promo",
    pipelineName: "promo_orchestration_pipeline_2",
    pipelineId: "c7474d03-d7d3-42e9-ad26-8d6b",
    issueDetail:
      "High resource consumption (CPU/memory) during large dataset transformations.",
    reportedBy: "Kaushik G.",
    aiAgentInvestigation: "Investigate",
    issueId: "weryu5-cucu-xghx-chss-67ahjch",
  },
  {
    serial_no: "04",
    date: "01/01/2023",
    usecase: "Promo",
    pipelineName: "price_orchestration_pipeline_1",
    pipelineId: "bhga-65ga-aiuc-chua-huah-atyth",
    issueDetail:
      "Data duplication detected in the orders table in the Data Warehouse after ETL process.",
    reportedBy: "Aakash S.",
    aiAgentInvestigation: "Investigate",
    issueId: "c7474d03-d7d3-42e9-ad26-8d6b",
  },
  {
    serial_no: "05",
    date: "15/03/2023",
    usecase: "Promo",
    pipelineName: "promo_orchestration_pipeline_2",
    pipelineId: "c7474d03-d7d3-42e9-ad26-8d6b",
    issueDetail: "Data corruption detected in the final output table.",
    reportedBy: "Aakash S.",
    aiAgentInvestigation: "Investigate",
    issueId: "90iy474d03-d7d3-42e9-ad26-8d6b",
  },
]

// Main component for Issue Tracker
export const IssueTracker = () => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(
    null,
  )
  const [selectedIssueDetail, setIssueDetail] = useState<string | null>(null)
  const [selectedIssueId, setIssueId] = useState<string | null>(null)
  const [order, setOrder] = useState<"asc" | "desc">("asc")
  const [orderBy, setOrderBy] = useState<string>("date")
  const classes = useStyles()

  // Handle page change in pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  // Handle change in rows per page in pagination
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  // Handle opening of dialog on button click and set the selected pipeline ID
  const handleInvestigateClick = (
    pipelineId: string,
    issueDetail: string,
    issueId: string,
  ) => {
    setSelectedPipelineId(pipelineId)
    setIssueDetail(issueDetail)
    setIssueId(issueId)
    setOpenDialog(true)
  }

  // Handle closing of dialog
  const handleCloseDialog = () => {
    setOpenDialog(false)
    setSelectedPipelineId(null)
  }

  // Handle sorting
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
  }

  // Sort data based on the order and orderBy
  const sortedData = mockData.sort((a, b) => {
    if (orderBy === "aiAgentInvestigation") return 0 // Skip sorting for button column
    if (a[orderBy as keyof typeof a] < b[orderBy as keyof typeof b]) {
      return order === "asc" ? -1 : 1
    }
    if (a[orderBy as keyof typeof a] > b[orderBy as keyof typeof b]) {
      return order === "asc" ? 1 : -1
    }
    return 0
  })

  return (
    <div>
      <Box display={"flex"} gap={2}>
        <Paper sx={{ width: "100%" }}>
          <TableContainer sx={{ maxHeight: 515, borderRadius: 10 }}>
            <Table
              stickyHeader
              aria-label="sticky table"
              sx={{ position: "relative", zIndex: 0 }}
            >
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      className={
                        column.id === "issueDetail"
                          ? classes.issueDetailHeader
                          : classes.wrapper
                      }
                      style={{ minWidth: column.minWidth }}
                    >
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={() => handleSort(column.id)}
                        classes={{ icon: classes.sortLabelIcon }} // Apply vertical sorting icon style
                      >
                        <Typography variant="body1">{column.label}</Typography>
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                      {columns.map((column) => {
                        const value = row[column.id as keyof typeof row]
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            className={
                              column.id === "issueDetail"
                                ? classes.issueDetailCell
                                : classes.wrapper
                            }
                          >
                            {column.id === "aiAgentInvestigation" ? (
                              <Button
                                variant="contained"
                                className={classes.investigateButton}
                                startIcon={
                                  <img
                                    src={InvestigateIcon}
                                    alt="Investigate"
                                    style={{ width: 20, height: 20 }}
                                  />
                                }
                                onClick={() =>
                                  handleInvestigateClick(
                                    row.pipelineId,
                                    row.issueDetail,
                                    row.issueId,
                                  )
                                }
                              >
                                Investigate
                              </Button>
                            ) : (
                              value
                            )}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={mockData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Dialog for investigation */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        className={classes.dialog_box}
      >
        <DialogContent className={classes.dialog_box_content}>
          <Box className={classes.upperPartition}>
            <Typography className={classes.heading}>
              Generating Ai Response
            </Typography>
            <Typography className={classes.subHeading}>
              The response has been Ai generated after analyzing all the fields.
            </Typography>
          </Box>
          <Box className={classes.lowerPartition}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography className={classes.boldText}>Issue ID:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography> {selectedIssueId} </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography className={classes.boldText}>Issue:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>
                  {selectedIssueDetail} Pipeline Id: {selectedPipelineId}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography className={classes.boldText}>Response:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>
                  Possible spell mismatch in ingestion data in table
                  staging_accounts in Lakehouse CDM.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  )
}
